import React from 'react';
import './DeviceScenarioVisual.css';

interface DeviceScenarioVisualProps {
  type: 'system-notification' | 'browser-warning' | 'usb-dialog' | 'network-selection' | 'software-install' | 'email-qr' | 'voice-message' | 'mfa-notifications' | 'antivirus-alert' | 'email-attachment-preview' | 'vscode-extension' | 'file-download-prompt';
  data: {
    title?: string;
    message?: string;
    icon?: string;
    buttons?: string[];
    url?: string;
    details?: string[];
    warningLevel?: 'low' | 'medium' | 'high';
    screenshot?: any;
    sender?: string;
    subject?: string;
    qrCodeUrl?: string;
    voiceTranscript?: string;
    notificationCount?: number;
    networks?: Array<{name: string; security: string; strength: string; highlighted?: boolean}>;
    publisher?: string;
    fileSize?: string;
    fileName?: string;
    attachmentType?: string;
    codeSnippet?: string;
    extensionName?: string;
    sourceUrl?: string;
  };
}

/**
 * Keep participant-facing screens stimulus-like. Scenario metadata may include
 * analyst explanations and teaching notes; those must not be rendered before
 * the participant decides.
 */
export const getParticipantVisibleDetails = (details: string[] = []): string[] => {
  const answerSectionHeading =
    /(security verification|verification check|security threat|security risk(?: assessment)?|possible malware|bluetooth attack|attack type|red flags|reality check|safer alternatives|security rule|recommendation|if accepted|real (?:driver )?updates|it security|why .*permissions.*(?:safe|needed))/i;
  const directAnswer =
    /(^\s*[✓✗❌]|this (?:is|was) (?:safe|legitimate|fake|dangerous)|safe to (?:approve|install|connect|use)|risk level\s*:|never (?:plug|pair)|attack type\s*:)/i;
  let suppressSection = false;

  return details.filter(detail => {
    const text = detail.trim();
    if (answerSectionHeading.test(text)) {
      suppressSection = true;
      return false;
    }
    if (text === '') {
      suppressSection = false;
      return true;
    }
    if (suppressSection || directAnswer.test(text)) {
      return false;
    }
    return true;
  });
};

const DeviceScenarioVisual: React.FC<DeviceScenarioVisualProps> = ({ type, data }) => {
  const visibleDetails = getParticipantVisibleDetails(data.details);


  // System Notification (Windows/Mac style)
  if (type === 'system-notification') {
    return (
      <div className="device-visual-container">
        <div className="system-notification">
          <div className="notification-header">
            <div className="notification-icon">{data.icon || '🔔'}</div>
            <div className="notification-title">{data.title || 'System Notification'}</div>
            <div className="notification-close">×</div>
          </div>
          <div className="notification-body">
            <p className="notification-message">{data.message}</p>
            {visibleDetails.length > 0 && (
              <div className="notification-details">
                {visibleDetails.map((detail, i) => (
                  <p key={i} className="detail-text">{detail}</p>
                ))}
              </div>
            )}
          </div>
          {/* Buttons removed - decision buttons handled by DeviceTask */}
        </div>
      </div>
    );
  }

  // Browser Security Warning
  if (type === 'browser-warning') {
    return (
      <div className="device-visual-container">
        <div className={`browser-warning warning-${data.warningLevel || 'medium'}`}>
          <div className="browser-header">
            <div className="browser-tabs">
              <div className="browser-tab active">
                <span className="tab-icon">🔒</span>
                <span className="tab-title">Security Warning</span>
                <span className="tab-close">×</span>
              </div>
            </div>
            <div className="browser-address-bar">
              <span className="address-icon">⚠️</span>
              <span className="address-url">{data.url || 'about:blank'}</span>
            </div>
          </div>
          <div className="browser-content">
            <div className="warning-icon-large">
              {data.warningLevel === 'high' ? '🚨' : data.warningLevel === 'medium' ? '⚠️' : 'ℹ️'}
            </div>
            <h2 className="warning-title">{data.title}</h2>
            <p className="warning-message">{data.message}</p>
            {visibleDetails.length > 0 && (
              <div className="warning-details">
                <p className="details-header"><strong>Technical Details:</strong></p>
                {visibleDetails.map((detail, i) => (
                  <p key={i} className="detail-item">• {detail}</p>
                ))}
              </div>
            )}
            {/* Buttons removed - decision buttons handled by DeviceTask */}
          </div>
        </div>
      </div>
    );
  }

  // USB Device Connection Dialog
  if (type === 'usb-dialog') {
    return (
      <div className="device-visual-container">
        <div className="usb-dialog">
          <div className="dialog-header">
            <span className="dialog-icon">💾</span>
            <span className="dialog-title">{data.title || 'USB Device Connected'}</span>
          </div>
          <div className="dialog-body">
            <div className="usb-device-icon">🔌</div>
            <p className="dialog-message">{data.message}</p>
            {visibleDetails.length > 0 && (
              <div className="device-details">
                {visibleDetails.map((detail, i) => (
                  <div key={i} className="detail-row">
                    <span className="detail-label">{detail.split(':')[0]}:</span>
                    <span className="detail-value">{detail.split(':')[1]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Buttons removed - decision buttons handled by DeviceTask */}
        </div>
      </div>
    );
  }

  // Network Selection Screen
  if (type === 'network-selection') {
    return (
      <div className="device-visual-container">
        <div className="network-selection">
          <div className="network-header">
            <h3>📶 Available Networks</h3>
          </div>
          <div className="network-list">
            {data.networks && data.networks.map((network, i) => (
              <div key={i} className={`network-item ${network.highlighted ? 'highlighted' : ''}`}>
                <div className="network-signal">{network.strength}</div>
                <div className="network-info">
                  <div className="network-name">{network.name}</div>
                  <div className="network-security">{network.security}</div>
                </div>
                {network.highlighted && <div className="network-badge">RECOMMENDED</div>}
              </div>
            ))}
          </div>
          <p className="network-message">{data.message}</p>
        </div>
      </div>
    );
  }

  // Software Installation Prompt
  if (type === 'software-install') {
    return (
      <div className="device-visual-container">
        <div className="software-install">
          <div className="install-header">
            <div className="install-icon">📦</div>
            <h3>{data.title || 'Software Installation'}</h3>
          </div>
          <div className="install-body">
            <p className="install-message">{data.message}</p>
            {visibleDetails.length > 0 && (
              <div className="install-details">
                <table className="details-table">
                  <tbody>
                    {visibleDetails.map((detail, i) => {
                      const [label, value] = detail.split(':');
                      return (
                        <tr key={i}>
                          <td className="detail-label">{label}:</td>
                          <td className="detail-value">{value}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {data.url && (
              <div className="install-source">
                <span className="source-label">Source:</span>
                <span className="source-url">{data.url}</span>
              </div>
            )}
          </div>
          {/* Buttons removed - decision buttons handled by DeviceTask */}
        </div>
      </div>
    );
  }

  // Email with QR Code (Phishing)
  if (type === 'email-qr') {
    return (
      <div className="device-visual-container">
        <div className="email-qr">
          <div className="email-header">
            <div className="email-from">
              <span className="email-label">From:</span>
              <span className="email-value">{data.sender || 'Unknown Sender'}</span>
            </div>
            <div className="email-subject">
              <span className="email-label">Subject:</span>
              <span className="email-value">{data.subject || 'No Subject'}</span>
            </div>
          </div>
          <div className="email-body">
            <div className="email-title">{data.title}</div>
            <p className="email-message" style={{ whiteSpace: 'pre-line' }}>{data.message}</p>

            {/* QR Code Display */}
            <div className="qr-code-container">
              <div className="qr-code-box">
                <div className="qr-code-pattern">
                  <div className="qr-row">▓▓░░▓▓░░▓▓▓░░▓▓░</div>
                  <div className="qr-row">░░▓▓▓▓░░░░▓▓▓▓▓░</div>
                  <div className="qr-row">▓▓░░░░▓▓▓▓░░░░▓▓</div>
                  <div className="qr-row">░░▓▓▓▓░░░░▓▓░░░░</div>
                  <div className="qr-row">▓▓░░▓▓▓▓▓▓▓▓░░▓▓</div>
                </div>
              </div>
              {data.qrCodeUrl && (
                <div className="qr-code-url-preview">
                  URL: {data.qrCodeUrl}
                </div>
              )}
            </div>

            {/* Buttons removed - decision buttons handled by DeviceTask */}

            {visibleDetails.length > 0 && (
              <div className="email-footer">
                {visibleDetails.map((detail, i) => (
                  <p key={i} className="email-footer-text">{detail}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Voice Message (AI Deepfake)
  if (type === 'voice-message') {
    return (
      <div className="device-visual-container">
        <div className="voice-message-phone">
          <div className="phone-header">
            <div className="phone-status-bar">
              <span className="phone-time">6:30 AM</span>
              <span className="phone-battery">🔋 98% 📶</span>
            </div>
            <div className="phone-nav-bar">
              <span className="phone-back">← Voicemail</span>
            </div>
          </div>

          <div className="phone-body">
            <div className="voicemail-card">
              <div className="voicemail-header">
                <span className="voicemail-icon">🎤</span>
                <span className="voicemail-title">{data.title || 'Voice Message'}</span>
              </div>

              {visibleDetails.length > 0 && (
                <div className="voicemail-details">
                  {visibleDetails.map((detail, i) => (
                    <div key={i} className="voicemail-detail-row">{detail}</div>
                  ))}
                </div>
              )}

              <div className="voicemail-message">{data.message}</div>

              {/* Play Progress Bar */}
              <div className="voicemail-player">
                <div className="play-button">▶</div>
                <div className="progress-bar">
                  <div className="progress-track"></div>
                  <div className="progress-fill" style={{width: '0%'}}></div>
                </div>
                <div className="time-display">0:00 / 0:47</div>
              </div>

              {data.voiceTranscript && (
                <div className="voicemail-transcript">
                  <div className="transcript-label">Transcript (Auto-generated):</div>
                  <div className="transcript-text">{data.voiceTranscript}</div>
                </div>
              )}

              {/* Buttons removed - decision buttons handled by DeviceTask */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MFA Push Notification Fatigue
  if (type === 'mfa-notifications') {
    const count = data.notificationCount || 12;
    const notifications = [];

    for (let i = 0; i < Math.min(count, 5); i++) {
      const timeOffset = i * 2;
      const time = `11:${47 - timeOffset} PM`;
      notifications.push(
        <div key={i} className="mfa-notification-item">
          <div className="mfa-notif-header">
            <span className="mfa-notif-icon">🔐</span>
            <span className="mfa-notif-app">Microsoft Authenticator</span>
            <span className="mfa-notif-time">{time}</span>
          </div>
          <div className="mfa-notif-body">Sign-in request</div>
          <div className="mfa-notif-action">Tap to approve or deny</div>
        </div>
      );
    }

    return (
      <div className="device-visual-container">
        <div className="mfa-phone-lockscreen">
          <div className="lockscreen-header">
            <div className="lockscreen-status">
              <span>🔋 45%</span>
              <span>Wed, Nov 15</span>
              <span>📶 ▰▰▰ 4G</span>
            </div>
            <div className="lockscreen-time">
              <div className="lockscreen-clock">11:47 PM</div>
              <div className="lockscreen-date">Wednesday, November 15</div>
            </div>
          </div>

          <div className="lockscreen-icon">🔒</div>

          <div className="lockscreen-notifications">
            <div className="notif-header">
              <span>🔔 NOTIFICATIONS ({count})</span>
            </div>
            <div className="notif-list">
              {notifications}
              {count > 5 && (
                <div className="notif-more">... and {count - 5} more</div>
              )}
            </div>
          </div>

          {/* When tapped, show detailed view */}
          <div className="mfa-detail-view">
            <div className="mfa-detail-header">
              <span className="mfa-back">← Back</span>
              <span className="mfa-app-name">Microsoft Authenticator</span>
            </div>

            <div className="mfa-detail-body">
              <div className="mfa-detail-title">🔐 Approve sign-in?</div>

              {visibleDetails.length > 0 && (
                <div className="mfa-detail-info">
                  {visibleDetails.map((detail, i) => (
                    <div key={i} className="mfa-info-row">{detail}</div>
                  ))}
                </div>
              )}

              <div className="mfa-warning-message">{data.message}</div>

              {/* Buttons removed - decision buttons handled by DeviceTask */}

              <div className="mfa-help-link">Need help? Report suspicious activity</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Antivirus Alert (Windows Defender style)
  if (type === 'antivirus-alert') {
    return (
      <div className="device-visual-container">
        <div className={`antivirus-alert warning-${data.warningLevel || 'high'}`}>
          <div className="antivirus-header">
            <div className="antivirus-icon">
              🛡️
            </div>
            <div className="antivirus-title">{data.title || 'Threat Detected'}</div>
          </div>
          <div className="antivirus-body">
            <div className="threat-icon-large">
              ⚠️
            </div>
            <h2 className="threat-title">{data.message}</h2>
            {data.fileName && (
              <div className="file-info">
                <div className="file-name">📄 {data.fileName}</div>
                {data.fileSize && <div className="file-size">Size: {data.fileSize}</div>}
              </div>
            )}
            {visibleDetails.length > 0 && (
              <div className="threat-details">
                {visibleDetails.map((detail, i) => (
                  <p key={i} className="detail-line">{detail}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Email Attachment Preview
  if (type === 'email-attachment-preview') {
    return (
      <div className="device-visual-container">
        <div className="email-attachment-preview">
          <div className="email-preview-header">
            <div className="email-meta">
              <div className="from-line">
                <span className="label">From:</span>
                <span className="value">{data.sender || 'Unknown'}</span>
              </div>
              <div className="subject-line">
                <span className="label">Subject:</span>
                <span className="value">{data.subject || 'No Subject'}</span>
              </div>
            </div>
          </div>
          <div className="email-preview-body">
            <p className="email-text" style={{ whiteSpace: 'pre-line' }}>{data.message}</p>

            {data.fileName && (
              <div className="attachment-card">
                <div className="attachment-icon">
                  {data.attachmentType?.includes('Excel') ? '📊' :
                   data.attachmentType?.includes('Word') ? '📝' :
                   data.attachmentType?.includes('PDF') ? '📄' : '📎'}
                </div>
                <div className="attachment-info">
                  <div className="attachment-name">{data.fileName}</div>
                  {data.fileSize && <div className="attachment-size">{data.fileSize}</div>}
                  {data.attachmentType && <div className="attachment-type">{data.attachmentType}</div>}
                </div>
              </div>
            )}

            {visibleDetails.length > 0 && (
              <div className="email-details-box">
                {visibleDetails.map((detail, i) => (
                  <div key={i} className="detail-row">{detail}</div>
                ))}
              </div>
            )}
          </div>
          {/* Buttons removed - decision buttons handled by DeviceTask */}
        </div>
      </div>
    );
  }

  // VS Code Extension / AI Suggestion
  if (type === 'vscode-extension') {
    return (
      <div className="device-visual-container">
        <div className="vscode-window">
          <div className="vscode-titlebar">
            <div className="vscode-tabs">
              <div className="vscode-tab active">
                <span className="file-icon">📄</span>
                <span className="file-name">database.js</span>
                <span className="close-btn">×</span>
              </div>
            </div>
          </div>
          <div className="vscode-main">
            <div className="vscode-sidebar">
              <div className="sidebar-icon active">📁</div>
              <div className="sidebar-icon">🔍</div>
              <div className="sidebar-icon">🔗</div>
              <div className="sidebar-icon">⚙️</div>
            </div>
            <div className="vscode-editor">
              <div className="editor-header">
                <div className="breadcrumb">src › config › database.js</div>
              </div>
              <div className="code-area">
                {data.codeSnippet && (
                  <pre className="code-suggestion">{data.codeSnippet}</pre>
                )}
                <div className="copilot-badge">
                  <span className="copilot-icon">🤖</span>
                  <span className="copilot-text">{data.extensionName || 'GitHub Copilot'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="vscode-statusbar">
            <div className="status-left">
              <span className="status-item">✓ {data.publisher}</span>
            </div>
            <div className="status-right">
              <span className="status-item">JavaScript</span>
            </div>
          </div>
          {visibleDetails.length > 0 && (
            <div className="extension-info-panel">
              {visibleDetails.map((detail, i) => (
                <div key={i} className="info-line">{detail}</div>
              ))}
            </div>
          )}
          {/* Buttons removed - decision buttons handled by DeviceTask */}
        </div>
      </div>
    );
  }

  // File Download Prompt (Browser style)
  if (type === 'file-download-prompt') {
    return (
      <div className="device-visual-container">
        <div className="file-download-prompt">
          <div className="download-header">
            <div className="browser-bar">
              <div className="browser-controls">
                <div className="control-btn">←</div>
                <div className="control-btn">→</div>
                <div className="control-btn">⟳</div>
              </div>
              <div className="address-bar">
                <span className="lock-icon">🔒</span>
                <span className="url">{data.sourceUrl || 'sharepoint.company.com'}</span>
              </div>
            </div>
          </div>
          <div className="download-body">
            <div className="download-icon">📥</div>
            <h3 className="download-title">{data.title}</h3>
            <p className="download-message">{data.message}</p>

            <div className="file-card">
              <div className="file-icon-large">📦</div>
              <div className="file-details">
                <div className="file-name-large">{data.fileName || 'file.zip'}</div>
                {data.fileSize && <div className="file-size-display">{data.fileSize}</div>}
                {data.sourceUrl && (
                  <div className="file-source">From: {data.sourceUrl}</div>
                )}
                {data.publisher && (
                  <div className="file-publisher">Publisher: {data.publisher}</div>
                )}
              </div>
            </div>

            {visibleDetails.length > 0 && (
              <div className="download-details-box">
                {visibleDetails.map((detail, i) => (
                  <div key={i} className="download-detail-line">{detail}</div>
                ))}
              </div>
            )}
          </div>
          {/* Buttons removed - decision buttons handled by DeviceTask */}
        </div>
      </div>
    );
  }

  return null;
};

export default DeviceScenarioVisual;
